/**
 * Personal Recipe Details Service
 *
 * Handles lazy loading of individual recipe details with LRU caching.
 * Only loads full recipe data when needed to minimize memory usage.
 */

import { Recipe } from '../types/recipe';
import personalRecipeAssets from './personalRecipeAssets';
import personalRecipeIndex from './personalRecipeIndex';

interface CacheEntry {
  recipe: Recipe;
  lastAccessed: number;
}

class PersonalRecipeDetailsService {
  private cache = new Map<string, CacheEntry>();
  private maxCacheSize = 30; // Keep 30 most recent recipes in memory
  private loadingPromises = new Map<string, Promise<Recipe | null>>();

  private bundledIds: Promise<Set<string>> | null = null;

  private isBundled(id: string): Promise<boolean> {
    this.bundledIds ??= personalRecipeIndex.getBundledIds()
      .catch(() => { this.bundledIds = null; return new Set<string>(); });
    return this.bundledIds.then((ids) => ids.size === 0 || ids.has(id));
  }

  /**
   * Get recipe details by ID with caching
   */
  async getRecipeDetails(id: string): Promise<Recipe | null> {
    // User-owned recipes only ever live in Firestore — there is no catalog asset
    // file for them, so the fetch below is a guaranteed 404 on every open. It
    // cost a round-trip and a console warning, and the added latency widened the
    // window for the stale-recipe race in #565.
    if (!id || id.startsWith('saved-') || id.startsWith('imported-')) {
      return null;
    }
    // Nor do catalogue recipes the app does not bundle: the planner plans from
    // all 1420 since plannerLibrary.ts, and the detail screen, meal-plan
    // translation and recipeService all ask here first — one 404 per recipe
    // before they reach Firestore. The bundled index says which files exist.
    if (!(await this.isBundled(id))) {
      return null;
    }

    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      cached.lastAccessed = Date.now();
      console.log(`PersonalRecipeDetails - Using cached recipe: ${id}`);
      return cached.recipe;
    }

    // Check if already loading
    if (this.loadingPromises.has(id)) {
      return this.loadingPromises.get(id)!;
    }

    // Start loading
    const loadPromise = this.loadRecipeFromFile(id);
    this.loadingPromises.set(id, loadPromise);

    try {
      const recipe = await loadPromise;
      return recipe;
    } finally {
      this.loadingPromises.delete(id);
    }
  }

  /**
   * Load recipe from individual JSON file
   */
  private async loadRecipeFromFile(id: string): Promise<Recipe | null> {
    try {
      console.log(`PersonalRecipeDetails - Loading recipe details: ${id}`);
      const startTime = Date.now();

      // Use the cross-platform asset loader
      const recipe = await personalRecipeAssets.loadRecipeDetails(id);

      if (recipe) {
        // Add to cache
        this.addToCache(id, recipe);

        const loadTime = Date.now() - startTime;
        console.log(`PersonalRecipeDetails - Loaded recipe ${id} in ${loadTime}ms`);

        return recipe;
      }

      return null;
    } catch (error) {
      console.error(`PersonalRecipeDetails - Error loading recipe ${id}:`, error);
      return null;
    }
  }

  /**
   * Add recipe to cache with LRU eviction
   */
  private addToCache(id: string, recipe: Recipe): void {
    // Remove if already exists
    if (this.cache.has(id)) {
      this.cache.delete(id);
    }

    // Add new entry
    this.cache.set(id, {
      recipe,
      lastAccessed: Date.now()
    });

    // Evict oldest entries if cache is full
    if (this.cache.size > this.maxCacheSize) {
      this.evictOldestEntries();
    }
  }

  /**
   * Evict oldest cache entries to maintain size limit
   */
  private evictOldestEntries(): void {
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

    const toRemove = entries.slice(0, entries.length - this.maxCacheSize);

    for (const [id] of toRemove) {
      this.cache.delete(id);
    }

    if (toRemove.length > 0) {
      console.log(`PersonalRecipeDetails - Evicted ${toRemove.length} old recipes from cache`);
    }
  }

  /**
   * Preload multiple recipes (for anticipated usage)
   */
  async preloadRecipes(ids: string[]): Promise<void> {
    console.log(`PersonalRecipeDetails - Preloading ${ids.length} recipes`);

    const loadPromises = ids
      .filter(id => !this.cache.has(id) && !this.loadingPromises.has(id))
      .map(id => this.getRecipeDetails(id));

    await Promise.allSettled(loadPromises);
  }

  /**
   * Clear cache (useful for memory management)
   */
  clearCache(): void {
    const cacheSize = this.cache.size;
    this.cache.clear();
    this.loadingPromises.clear();
    console.log(`PersonalRecipeDetails - Cleared cache (${cacheSize} entries)`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; maxSize: number; memoryUsageKB: number } {
    // Rough estimate of memory usage (each recipe ~5KB)
    const memoryUsageKB = this.cache.size * 5;

    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      memoryUsageKB
    };
  }

  /**
   * Check if recipe is cached
   */
  isCached(id: string): boolean {
    return this.cache.has(id);
  }

  /**
   * Get multiple recipes efficiently
   */
  async getMultipleRecipes(ids: string[]): Promise<(Recipe | null)[]> {
    console.log(`PersonalRecipeDetails - Loading ${ids.length} recipes`);

    const promises = ids.map(id => this.getRecipeDetails(id));
    return Promise.all(promises);
  }

  /**
   * Warmup cache with most popular recipes
   */
  async warmupCache(popularRecipeIds: string[]): Promise<void> {
    const toWarmup = popularRecipeIds
      .slice(0, Math.min(10, this.maxCacheSize)) // Warmup up to 10 recipes
      .filter(id => !this.cache.has(id));

    if (toWarmup.length > 0) {
      console.log(`PersonalRecipeDetails - Warming up cache with ${toWarmup.length} popular recipes`);
      await this.preloadRecipes(toWarmup);
    }
  }

  /**
   * Convert personal recipe to match the expected Recipe interface
   */
  private normalizePersonalRecipe(personalRecipe: any): Recipe {
    return {
      ...personalRecipe,
      // Ensure all required Recipe properties are present
      id: parseInt(personalRecipe.id) || 0,
      title: personalRecipe.title || 'Untitled Recipe',
      servings: parseInt(personalRecipe.servings) || 1,
      readyInMinutes: personalRecipe.readyInMinutes || 0,
      cookingMinutes: personalRecipe.cookingMinutes || 0,
      preparationMinutes: personalRecipe.preparationMinutes || 0,
      healthScore: personalRecipe.healthScore || 0,
      cuisines: personalRecipe.cuisines || [],
      dishTypes: personalRecipe.dishTypes || [],
      diets: personalRecipe.diets || [],
      occasions: personalRecipe.occasions || [],
      instructions: personalRecipe.instructions || [],
      extendedIngredients: personalRecipe.extendedIngredients || [],

      // Personal recipe specific fields
      isPersonalRecipe: true,
      personalRecipeId: personalRecipe.id
    };
  }
}

export default new PersonalRecipeDetailsService();